import React, {useEffect, useState, useMemo} from "react";
import { BarChart } from "@mui/x-charts";
import { 
  ButtonGroup, Card, Paper,
  FormControl, InputLabel, MenuItem, Button,
  Select, Typography, Box, Divider, Stack, Grid,
  Chip, IconButton, Tooltip
} from "@mui/material";
import { 
  Assessment,
  FilterList,
  Sort
} from "@mui/icons-material";

const PAGE_SIZE = 20;

const AssessmentsBChart = ({ data }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState('assessment_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedMetrics, setSelectedMetrics] = useState(['totals', 'average']);
  
  // Process and sort data
  const processedData = useMemo(() => {
    if (!data) return [];
    
    const sorted = [...data].sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case 'assessment_name':
          aVal = a.assessment_name.toLowerCase();
          bVal = b.assessment_name.toLowerCase();
          break;
        case 'assessments_total':
          aVal = a.assessments_total;
          bVal = b.assessments_total;
          break;
        case 'assessments_average':
          aVal = a.assessments_average;
          bVal = b.assessments_average;
          break;
        case 'min_score':
          aVal = a.min_score;
          bVal = b.min_score;
          break;
        case 'max_score':
          aVal = a.max_score;
          bVal = b.max_score;
          break;
        default:
          aVal = a[sortBy];
          bVal = b[sortBy];
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });
    
    return sorted;
  }, [data, sortBy, sortOrder]);
  
  // Paginate
  const paginatedData = useMemo(() => {
    const startIdx = currentPage * PAGE_SIZE;
    return processedData.slice(startIdx, startIdx + PAGE_SIZE);
  }, [processedData, currentPage]);

  const totalPages = Math.ceil((processedData?.length || 0) / PAGE_SIZE);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const toggleMetric = (metric) => {
    setSelectedMetrics(prev =>
      prev.includes(metric)
        ? prev.filter(m => m !== metric)
        : [...prev, metric]
    );
  };

  // Get series configuration based on selected metrics
  const getSeries = () => {
    const series = [];
    
    if (selectedMetrics.includes('totals')) {
      series.push({
        dataKey: 'assessments_total',
        label: 'Total Assessments',
        color: '#1976d2',
      });
    }
    
    if (selectedMetrics.includes('average')) {
      series.push({
        dataKey: 'assessments_average',
        label: 'Average Score',
        color: '#2e7d32',
        type: selectedMetrics.length > 1 ? 'line' : 'bar',
      });
    }
    
    if (selectedMetrics.includes('min')) {
      series.push({
        dataKey: 'min_score',
        label: 'Min Score',
        color: '#ff9800',
      });
    }
    
    if (selectedMetrics.includes('max')) {
      series.push({
        dataKey: 'max_score',
        label: 'Max Score',
        color: '#d32f2f',
      });
    }
    
    return series;
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h6" fontWeight="bold">
                Assessment Analytics
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Showing {paginatedData.length} of {data?.length || 0} assessments
            </Typography>
          </Box>
          
          {/* Sort Controls */}
          {processedData.length > 0 && <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Sort by:
            </Typography>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <Select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  setSortOrder('asc');
                }}
                displayEmpty
              >
                <MenuItem value="assessment_name">Name</MenuItem>
                <MenuItem value="assessments_total">Total</MenuItem>
                <MenuItem value="assessments_average">Average</MenuItem>
                <MenuItem value="min_score">Min Score</MenuItem>
                <MenuItem value="max_score">Max Score</MenuItem>
              </Select>
            </FormControl>
            <Tooltip title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}>
              <IconButton 
                size="small" 
                onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                color="primary"
              >
                <Sort sx={{ 
                  transform: sortOrder === 'desc' ? 'rotate(180deg)' : 'none',
                  transition: 'transform 0.2s'
                }} />
              </IconButton>
            </Tooltip>
          </Stack>}
        </Stack>
        
        <Divider />
      </Box>

      {/* Metric Selector */}
      {processedData.length > 0 && <Paper sx={{ p: 1, mb: 2 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 0.75,
            fontWeight: 'medium',
            fontSize: { xs: '0.875rem', sm: 'inherit' }
          }}
        >
          Display Metrics:
        </Typography>
        
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {[
            { key: 'totals', label: 'Total', color: 'primary' },
            { key: 'average', label: 'Average', color: 'success' },
            { key: 'min', label: 'Min', color: 'warning' },
            { key: 'max', label: 'Max', color: 'error' }
          ].map(({ key, label, color }) => (
            <Chip
              key={key}
              label={label}
              size="small"
              onClick={() => toggleMetric(key)}
              color={selectedMetrics.includes(key) ? color : 'default'}
              variant={selectedMetrics.includes(key) ? 'filled' : 'outlined'}
              sx={{ 
                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                height: { xs: 24, sm: 32 },
                fontWeight: selectedMetrics.includes(key) ? 'bold' : 'normal',
              }}
            />
          ))}
        </Box>
      </Paper>}

      {/* Chart Area */}
      <Box sx={{ p: 0, height: "100%", width: "100%" }}>
        <BarChart
          dataset={paginatedData}
          height={450}
          xAxis={[{ 
            scaleType: 'band', 
            dataKey: 'assessment_name',
            label: '',
            tickLabelStyle: {
              angle: 0,
              textAnchor: 'end',
              fontSize: 11
            }
          }]}
          yAxis={[{
            label: selectedMetrics.includes('average') && selectedMetrics.includes('totals') 
              ? 'Score / Count' 
              : selectedMetrics.includes('average') 
                ? 'Score' 
                : 'Count',
            min: 0,
          }]}
          series={getSeries()}
          margin={{ top: 50, right: 20, left: 40, bottom: 50 }}
          
          slotProps={{ 
            legend: paginatedData.length > 0 ? {
              direction: 'row', 
              position: { vertical: 'top', horizontal: 'middle' }, 
              padding: -5 
            } : {
              hidden: true  // Hide legend when no data
            }
          }}
        />
        
        {/* Page Indicator */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Page {currentPage + 1} of {totalPages}
          </Typography>
        </Box>
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <ButtonGroup variant="outlined" size="small">
            <Button 
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
            >
              First
            </Button>
            <Button 
              onClick={() => setCurrentPage(p => Math.max(p - 1, 0))}
              disabled={currentPage === 0}
            >
              Previous
            </Button>
            <Button disabled>
              {currentPage + 1} / {totalPages}
            </Button>
            <Button 
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages - 1))}
              disabled={currentPage === totalPages - 1}
            >
              Next
            </Button>
            <Button 
              onClick={() => setCurrentPage(totalPages - 1)}
              disabled={currentPage === totalPages - 1}
            >
              Last
            </Button>
          </ButtonGroup>
        </Box>
      )}
    </Box>
  );
};

export default AssessmentsBChart;